import { ChartDataset } from "state/ui/editor/elements/ChartElement";

export interface LinearScale {
  domain: [number, number];
  range: [number, number];
  scale(value: number): number;
}

export interface BandScale {
  domain: string[];
  range: [number, number];
  paddingInner: number;
  bandwidth: number;
  step: number;
  scale(value: string): number;
  scaleAt(index: number): number;
}

export const linearScale = (
  domain: [number, number],
  range: [number, number]
): LinearScale => {
  const [d0, d1] = domain;
  const [r0, r1] = range;
  const dSpan = d1 - d0 || 1;
  return {
    domain,
    range,
    scale: value => r0 + ((value - d0) / dSpan) * (r1 - r0)
  };
};

export const bandScale = (
  domain: string[],
  range: [number, number],
  paddingInner = 0.2
): BandScale => {
  const [r0, r1] = range;
  const n = Math.max(domain.length, 1);
  const totalSpan = r1 - r0;
  const step = totalSpan / n;
  const bandwidth = step * (1 - paddingInner);
  const scaleAt = (index: number) => {
    if (index < 0 || index >= domain.length) return r0;
    return r0 + index * step + (step - bandwidth) / 2;
  };
  return {
    domain,
    range,
    paddingInner,
    step,
    bandwidth,
    scaleAt,
    scale: value => scaleAt(domain.indexOf(value))
  };
};

const niceStep = (rough: number): number => {
  if (rough <= 0) return 1;
  const exp = Math.floor(Math.log10(rough));
  const base = Math.pow(10, exp);
  const mantissa = rough / base;
  let nice: number;
  if (mantissa < 1.5) nice = 1;
  else if (mantissa < 3.5) nice = 2;
  else if (mantissa < 7.5) nice = 5;
  else nice = 10;
  return nice * base;
};

export const generateTicks = (
  min: number,
  max: number,
  targetCount = 5
): number[] => {
  if (!Number.isFinite(min) || !Number.isFinite(max) || min === max) {
    return [min];
  }
  const rough = (max - min) / targetCount;
  const step = niceStep(rough);
  const start = Math.ceil(min / step) * step;
  const ticks: number[] = [];
  for (let tick = start; tick <= max + 1e-9; tick += step) {
    ticks.push(Number(tick.toFixed(10)));
  }
  return ticks;
};

export const formatTickLabel = (value: number): string => {
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1)}B`;
  if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  if (Number.isInteger(value)) return String(value);
  return value.toFixed(2);
};

export const flattenValues = (dataset: ChartDataset): number[] =>
  dataset.rows.flatMap(row => row.values);

export const datasetDomain = (dataset: ChartDataset): [number, number] => {
  const values = flattenValues(dataset);
  if (!values.length) return [0, 1];
  const min = Math.min(0, ...values);
  const max = Math.max(0, ...values);
  return [min, max === min ? max + 1 : max];
};

// Neutral fallback when a chart's palette is empty (e.g. a template/API chart
// with seriesColors: []). Without it, `index % 0` is NaN and the lookup yields
// `undefined`, which SVG paints as black.
export const SERIES_COLOR_FALLBACK = "#333333";

// Series colour by index, cycling the palette. Guards the empty-palette case
// (`index % 0` is NaN, so the lookup is undefined) so renderers/legend never
// emit an undefined fill. Mirrors buildPieSlices below.
export const seriesColorAt = (colors: string[], index: number): string =>
  colors[index % colors.length] ?? SERIES_COLOR_FALLBACK;

export interface PieSlice {
  value: number;
  label: string;
  startAngle: number;
  endAngle: number;
  color: string;
}

export const buildPieSlices = (
  values: number[],
  labels: string[],
  colors: string[]
): PieSlice[] => {
  const paddedValues =
    values.length >= labels.length
      ? values
      : [
          ...values,
          ...Array.from({ length: labels.length - values.length }, () => 0)
        ];
  const total = paddedValues.reduce((sum, v) => sum + (v > 0 ? v : 0), 0);
  if (total === 0) return [];
  let startAngle = -Math.PI / 2;
  return paddedValues.map((value, index) => {
    const safeValue = value > 0 ? value : 0;
    const sweep = (safeValue / total) * Math.PI * 2;
    const slice: PieSlice = {
      value: safeValue,
      label: labels[index] ?? "",
      startAngle,
      endAngle: startAngle + sweep,
      color: colors[index % colors.length] ?? "#333333"
    };
    startAngle += sweep;
    return slice;
  });
};

export const describeArc = (
  cx: number,
  cy: number,
  radius: number,
  startAngle: number,
  endAngle: number,
  innerRadius = 0
): string => {
  const sweep = endAngle - startAngle;
  const largeArc = sweep > Math.PI ? 1 : 0;

  // A slice spanning the whole circle (a single 100% value) has identical start
  // and end points, and an SVG arc with coincident endpoints renders nothing.
  // Draw a full circle/ring as two semicircle arcs instead.
  const isFullCircle = sweep >= Math.PI * 2 - 1e-6;

  const x0 = cx + radius * Math.cos(startAngle);
  const y0 = cy + radius * Math.sin(startAngle);
  const x1 = cx + radius * Math.cos(endAngle);
  const y1 = cy + radius * Math.sin(endAngle);

  if (innerRadius <= 0) {
    if (isFullCircle) {
      const top = cy - radius;
      const bottom = cy + radius;
      return `M ${cx} ${top} A ${radius} ${radius} 0 1 1 ${cx} ${bottom} A ${radius} ${radius} 0 1 1 ${cx} ${top} Z`;
    }
    return `M ${cx} ${cy} L ${x0} ${y0} A ${radius} ${radius} 0 ${largeArc} 1 ${x1} ${y1} Z`;
  }

  if (isFullCircle) {
    const oTop = cy - radius;
    const oBottom = cy + radius;
    const iTop = cy - innerRadius;
    const iBottom = cy + innerRadius;
    // Outer ring clockwise, inner ring counter-clockwise so the nonzero
    // winding rule cuts the donut hole.
    return [
      `M ${cx} ${oTop}`,
      `A ${radius} ${radius} 0 1 1 ${cx} ${oBottom}`,
      `A ${radius} ${radius} 0 1 1 ${cx} ${oTop}`,
      "Z",
      `M ${cx} ${iTop}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${iBottom}`,
      `A ${innerRadius} ${innerRadius} 0 1 0 ${cx} ${iTop}`,
      "Z"
    ].join(" ");
  }

  const ix0 = cx + innerRadius * Math.cos(endAngle);
  const iy0 = cy + innerRadius * Math.sin(endAngle);
  const ix1 = cx + innerRadius * Math.cos(startAngle);
  const iy1 = cy + innerRadius * Math.sin(startAngle);

  return [
    `M ${x0} ${y0}`,
    `A ${radius} ${radius} 0 ${largeArc} 1 ${x1} ${y1}`,
    `L ${ix0} ${iy0}`,
    `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${ix1} ${iy1}`,
    "Z"
  ].join(" ");
};

export interface PlotArea {
  left: number;
  top: number;
  width: number;
  height: number;
}

export const xAxisLabelGutterFor = (fontSize: number) => fontSize * 1.6;
export const legendGutterFor = (fontSize: number) => Math.max(24, fontSize * 2);
export const yAxisGutterFor = (fontSize: number) => Math.max(40, fontSize * 3);
export const xAxisLabelOffsetFor = (fontSize: number) => fontSize + 2;

export const defaultPlotArea = (
  width: number,
  height: number,
  opts: { showLegend: boolean; showAxes: boolean; fontSize: number }
): PlotArea => {
  const padding = 12;
  const yAxisGutter = opts.showAxes ? yAxisGutterFor(opts.fontSize) : padding;
  const xAxisLabelGutter = opts.showAxes
    ? xAxisLabelGutterFor(opts.fontSize)
    : 0;
  const legendGutter = opts.showLegend ? legendGutterFor(opts.fontSize) : 0;
  const bottomGutter = padding + xAxisLabelGutter + legendGutter;
  return {
    left: yAxisGutter,
    top: padding,
    width: Math.max(width - yAxisGutter - padding, 1),
    height: Math.max(height - bottomGutter, 1)
  };
};
